import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getDiagramAvailability } from "../../actions";
import { quickCheckAccess } from "../../utils/accessMapFunctions";
import { useViewConfig } from "../app/ViewConfigContext";
import { formatLabel, typeInfo } from "../../utils/viewConfig";
import { BADGE, DEFAULT_CAP, PAINT, TEXT, badgeArrow, badgeCentre, bandNote, layout, legendText, tint } from "./generatedLayout";

// The diagram we draw for an object that has parts.
//
// `generatedLayout.js` decides where everything goes; this draws it, and
// makes it behave like the rest of the product: zoom, pan, fit, click to
// select, double-click or Enter to go one level in, Tab to move between
// parts. Nothing here knows a type name - the colours come from the view
// configuration, the sections from the relationships the server grouped the
// parts by.
//
// One SVG, no library, no new dependency.

const MIN_SCALE = 0.2;
const MAX_SCALE = 3;
const PAN_STEP = 60;
// How far the pointer must travel before the picture starts to follow it.
const DRAG_THRESHOLD = 4;
// The parts of the drawing that are controls rather than canvas.
const INTERACTIVE = ".bf-generated__card, .bf-generated__object, .bf-generated__more";

const reducedMotion = () => {
  try {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (err) {
    return false;
  }
};

// The ink, the text sizes and the offsets inside a box live with the geometry
// (`generatedLayout.js` PAINT/TEXT/BADGE), because the server's SVG writer
// draws the same picture from the same numbers.

const GeneratedCanvas = forwardRef(function GeneratedCanvas(
  { object, items = [], truncated = false, total = 0, flowOrdered = false, subscription, selectedGuid, onSelect, onOpen },
  ref
) {
  const config = useViewConfig();
  const hostRef = useRef(null);
  const [size, setSize] = useState({ width: 1024, height: 640 });
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [diagrams, setDiagrams] = useState({});
  const drag = useRef(null);
  // The fit is recomputed whenever the picture or the room changes, unless
  // the reader has moved the view themselves - their own view is never taken
  // away from them by a refresh.
  const moved = useRef(false);

  // Which parts have a diagram of their own: one request for the whole
  // picture, the same one the Flow's badges use.
  useEffect(() => {
    let active = true;
    const guids = items.map((item) => item?.GUID).filter(Boolean);
    if (guids.length === 0) {
      setDiagrams({});
      return undefined;
    }
    getDiagramAvailability(guids)
      .then((found) => {
        if (active) setDiagrams(found || {});
      })
      .catch(() => {
        // A picture is worth more than its badges: a part still opens on its
        // own map where it has parts.
        if (active) setDiagrams({});
      });
    return () => {
      active = false;
    };
  }, [items]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      const box = host.getBoundingClientRect();
      if (box.width > 0 && box.height > 0) setSize({ width: box.width, height: box.height });
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const cap = Number(config?.settings?.["diagram.generatedMaxParts"]);

  const drawing = useMemo(() => {
    const parts = items.map((item) => {
      const info = typeInfo(config, item?.Type);
      const formats = diagrams[item?.GUID] || [];
      const reachable = quickCheckAccess(item?.inTrial, subscription);
      return {
        ...item,
        label: info.label,
        colour: info.colour,
        hasDiagram: formats.length > 0,
        opens: reachable && (formats.length > 0 || item?.hasAssignment === true),
      };
    });
    const info = typeInfo(config, object?.Type || object?.type);
    return layout({
      object: {
        guid: object?.GUID || object?.guid || "",
        name: object?.name || "",
        type: object?.Type || object?.type || "",
        label: info.label,
        colour: info.colour,
      },
      parts,
      width: size.width,
      ordered: Boolean(flowOrdered),
      cap: Number.isFinite(cap) && cap > 0 ? cap : DEFAULT_CAP,
    });
  }, [items, diagrams, config, object, size.width, flowOrdered, subscription, cap]);

  const fit = useCallback(
    (mode) => {
      const room = { width: size.width, height: size.height };
      if (!room.width || !room.height || !drawing.width) return;
      const byWidth = room.width / drawing.width;
      // The width always fits, and the drawing is never blown up past its own
      // size. Where it is also short enough it is centred and whole; where it
      // is taller than the room it starts at the top and is read downwards,
      // as a document is - shrinking a tall picture until its last card fits
      // the screen is how the letters became unreadable on a phone.
      const scale = Math.max(MIN_SCALE, Math.min(byWidth, mode === "width" ? MAX_SCALE : 1));
      const tall = drawing.height * scale > room.height;
      setView({
        x: (room.width - drawing.width * scale) / 2,
        y: tall || mode === "width" ? 0 : (room.height - drawing.height * scale) / 2,
        scale,
      });
      moved.current = mode === "width";
    },
    [drawing.width, drawing.height, size.width, size.height]
  );

  // The whole picture, on open and whenever it changes shape - but never over
  // a view the reader chose for themselves.
  useEffect(() => {
    if (moved.current) return;
    fit();
  }, [fit]);

  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      moved.current = true;
      setView((v) => ({ ...v, scale: Math.min(MAX_SCALE, v.scale * 1.2) }));
    },
    zoomOut: () => {
      moved.current = true;
      setView((v) => ({ ...v, scale: Math.max(MIN_SCALE, v.scale / 1.2) }));
    },
    fit: () => {
      moved.current = false;
      fit();
    },
    fitWidth: () => fit("width"),
  }));

  const onWheel = (event) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      moved.current = true;
      const box = hostRef.current?.getBoundingClientRect();
      const px = event.clientX - (box?.left || 0);
      const py = event.clientY - (box?.top || 0);
      setView((v) => {
        const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, v.scale * (event.deltaY < 0 ? 1.12 : 1 / 1.12)));
        const ratio = scale / v.scale;
        return { scale, x: px - (px - v.x) * ratio, y: py - (py - v.y) * ratio };
      });
      return;
    }
    moved.current = true;
    setView((v) => ({ ...v, x: v.x - event.deltaX, y: v.y - event.deltaY }));
  };

  // Panning the picture, without ever taking a click away from a card.
  //
  // Two rules, and the second is the one that matters: a drag that starts on
  // a card is not a drag at all (the card is a control - it is clicked,
  // double-clicked and opened), and the canvas never captures the pointer.
  // Capturing it retargets every later pointer event to the canvas, and the
  // browser then raises `click` and `dblclick` on the canvas rather than on
  // the card underneath - which is exactly how a card stops opening on a
  // double-click while every test that fires one on the card still passes.
  const onPointerDown = (event) => {
    // A right- or middle-click is the browser's; a touch reports no button
    // at all, and it pans like a finger should.
    if (event.button > 0) return;
    if (event.target?.closest?.(INTERACTIVE)) return;
    drag.current = { x: event.clientX, y: event.clientY, view };
  };

  const onPointerMove = (event) => {
    if (!drag.current) return;
    const from = drag.current;
    const dx = event.clientX - from.x;
    const dy = event.clientY - from.y;
    // A hand shakes; a drag is deliberate. Below this the view doesn't move,
    // so a click never turns into a pan.
    if (!from.panning && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    drag.current = { ...from, panning: true };
    moved.current = true;
    setView({ ...from.view, x: from.view.x + dx, y: from.view.y + dy });
  };

  const endDrag = () => {
    drag.current = null;
  };

  const onHostKeyDown = (event) => {
    const steps = { ArrowUp: [0, PAN_STEP], ArrowDown: [0, -PAN_STEP], ArrowLeft: [PAN_STEP, 0], ArrowRight: [-PAN_STEP, 0] };
    const step = steps[event.key];
    // Only the canvas itself pans; a focused part keeps the arrow keys for
    // moving the page, and Enter for opening.
    if (!step || event.target !== event.currentTarget) return;
    event.preventDefault();
    moved.current = true;
    setView((v) => ({ ...v, x: v.x + step[0], y: v.y + step[1] }));
  };

  const partFor = (guid) => items.find((item) => item?.GUID === guid) || null;

  const openCard = (card) => {
    if (!card.opens) return;
    onOpen?.(card.guid, card.hasDiagram ? undefined : "structure");
  };

  const cardKeyDown = (event, card) => {
    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      event.stopPropagation();
      if (card.opens) openCard(card);
      else onSelect?.(partFor(card.guid));
    }
  };

  const m = drawing;
  const still = reducedMotion();

  return (
    <Box
      ref={hostRef}
      className="bf-generated"
      tabIndex={0}
      role="group"
      aria-label={`Diagram of ${object?.name || "this object"}`}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onHostKeyDown}
      sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden", touchAction: "none", bgcolor: "#fff" }}
    >
      <svg
        width="100%"
        height="100%"
        role="img"
        aria-label={`${m.shown} of ${total || m.total} parts, drawn`}
        style={{ display: "block", cursor: drag.current ? "grabbing" : "grab" }}
      >
        <defs>
          <marker id="bf-gen-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={PAINT.arrow} />
          </marker>
        </defs>
        <g
          transform={`translate(${view.x} ${view.y}) scale(${view.scale})`}
          style={still ? undefined : { transition: drag.current ? "none" : "transform 0.12s ease-out" }}
        >
          {m.edges.map((edge, index) => (
            <path
              key={`edge-${index}`}
              d={edge.d}
              fill="none"
              stroke={edge.kind === "belongs" ? PAINT.rail : PAINT.arrow}
              strokeWidth={edge.kind === "belongs" ? 1.25 : 1.5}
              strokeDasharray={edge.kind === "belongs" ? "4 4" : undefined}
              markerEnd={edge.kind === "next" ? "url(#bf-gen-arrow)" : undefined}
            />
          ))}

          {/* The object itself: what everything below belongs to. */}
          <g
            className="bf-generated__object"
            role="button"
            tabIndex={0}
            aria-label={`${m.title.name}, this object`}
            onClick={() => onSelect?.(object)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onSelect?.(object);
              }
            }}
          >
            <rect
              x={m.title.x}
              y={m.title.y}
              width={m.title.width}
              height={m.title.height}
              rx="12"
              fill={tint(m.title.colour, 0.08)}
              stroke={tint(m.title.colour, 0.35)}
              strokeWidth="1.5"
            />
            <rect x={m.title.x} y={m.title.y + 12} width="4" height={m.title.height - 24} rx="2" fill={m.title.colour} />
            <text
              x={m.title.x + TEXT.object.x}
              y={m.title.y + TEXT.object.typeY}
              fontSize={TEXT.object.typeSize}
              fontWeight="700"
              letterSpacing="0.08em"
              fill={PAINT.ink}
              opacity="0.66"
            >
              {String(m.title.typeLabel || "").toUpperCase()}
            </text>
            {m.title.lines.map((line, index) => (
              <text
                key={`title-${index}`}
                x={m.title.x + TEXT.object.x}
                y={m.title.y + TEXT.object.firstLine + index * TEXT.object.lineHeight}
                fontSize={TEXT.object.size}
                fontWeight="700"
                fill={PAINT.ink}
              >
                {line}
              </text>
            ))}
          </g>

          {m.bands.map((band, index) => (
            <g key={`band-${band.relationship}-${index}`}>
              {/* What this section is: the relationship's own label, and in
                  plain words how many parts are in it and which way it runs.
                  No arrow glyph - a reader shouldn't have to know what one
                  means in a graph. */}
              <text x={band.x} y={band.y + TEXT.band.textY} fontSize={TEXT.band.size} fontWeight="700" letterSpacing="0.07em" fill={PAINT.heading}>
                {String(band.label || "").toUpperCase()}
                <tspan fill={PAINT.headingCount} fontWeight="600" letterSpacing="0.02em">
                  {bandNote(band)}
                </tspan>
              </text>
              <line x1={band.x} y1={band.y + TEXT.band.ruleY} x2={band.x + band.width} y2={band.y + TEXT.band.ruleY} stroke={PAINT.rule} strokeWidth="1" />
            </g>
          ))}

          {m.cards.map((card, index) =>
            card.kind === "more" ? (
              <g
                key={`more-${index}`}
                className="bf-generated__more"
                role="button"
                tabIndex={0}
                aria-label={`${card.more} more parts, not drawn`}
                onClick={() => onSelect?.(object)}
              >
                <rect
                  x={card.x}
                  y={card.y}
                  width={card.width}
                  height={card.height}
                  rx="10"
                  fill={PAINT.moreFill}
                  stroke={PAINT.moreStroke}
                  strokeDasharray="5 4"
                />
                <text x={card.x + card.width / 2} y={card.y + card.height / 2 + 4} textAnchor="middle" fontSize="12.5" fill={PAINT.moreInk}>
                  and {card.more} more
                </text>
              </g>
            ) : (
              <g
                key={card.guid || `card-${index}`}
                className={`bf-generated__card${selectedGuid === card.guid ? " is-selected" : ""}${card.opens ? " is-openable" : ""}`}
                role="button"
                tabIndex={0}
                aria-label={`${card.name}${card.typeLabel ? `, ${card.typeLabel}` : ""}${card.opens ? ", opens" : ""}`}
                onClick={() => onSelect?.(partFor(card.guid))}
                onDoubleClick={() => openCard(card)}
                onKeyDown={(event) => cardKeyDown(event, card)}
              >
                <rect
                  x={card.x}
                  y={card.y}
                  width={card.width}
                  height={card.height}
                  rx="10"
                  fill={tint(card.colour, 0.1)}
                  stroke={selectedGuid === card.guid ? card.colour : tint(card.colour, 0.34)}
                  strokeWidth={selectedGuid === card.guid ? 2.5 : 1.25}
                />
                <rect x={card.x + 8} y={card.y + 10} width="3.5" height={card.height - 20} rx="1.75" fill={card.colour} />
                <text x={card.x + TEXT.card.x} y={card.y + TEXT.card.typeY} fontSize={TEXT.card.typeSize} fontWeight="700" letterSpacing="0.06em" fill={PAINT.ink} opacity="0.6">
                  {String(card.typeLabel || "").toUpperCase()}
                </text>
                {card.lines.map((line, lineIndex) => (
                  <text
                    key={`line-${lineIndex}`}
                    x={card.x + TEXT.card.x}
                    y={card.y + TEXT.card.firstLine + lineIndex * TEXT.card.lineHeight}
                    fontSize={TEXT.card.size}
                    fontWeight="600"
                    fill={PAINT.ink}
                  >
                    {line}
                  </text>
                ))}
                {/* The same mark the Flow puts on a step that has a diagram
                    of its own, and the same bargain: one click on the mark
                    opens it, where the card itself needs two. It is the
                    card's own action drawn where a mouse expects it, so it
                    stays out of the tab order - the card is the control. */}
                {card.opens && (
                  <g
                    className="bf-generated__badge"
                    aria-hidden="true"
                    onClick={(event) => {
                      event.stopPropagation();
                      openCard(card);
                    }}
                  >
                    <circle cx={badgeCentre(card).cx} cy={badgeCentre(card).cy} r={BADGE.radius} fill={PAINT.badge} />
                    <path
                      d={badgeArrow(card)}
                      stroke={PAINT.badgeMark}
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <title>Open this</title>
                  </g>
                )}
                {/* The browser's own tooltip says what to do, not what the
                    card already says: the name is drawn on it. */}
                {card.opens && <title>Double-click, or press Enter, to open this</title>}
              </g>
            )
          )}
        </g>
      </svg>

      <Box className="bf-generated__legend">
        <Typography variant="caption" component="span">
          {legendText(m, { total, truncated, flowName: formatLabel(config, "bpmn") })}
        </Typography>
      </Box>
    </Box>
  );
});

export default GeneratedCanvas;
